"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ComponentDropdown } from "./ComponentDropdown"
import type { ComponentHierarchyItem, DropdownState } from "./types"
import { extractComponentInfo, findReactFiber, getEditorUrl } from "./utils"

interface ClickToComponentProps {
  editor?: string
  projectRoot?: string
}

export function ClickToComponent({
  editor = "cursor",
  projectRoot = "frontend",
}: ClickToComponentProps) {
  const [dropdown, setDropdown] = useState<DropdownState | null>(null)
  const [isOptionPressed, setIsOptionPressed] = useState(false)
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [showTooltip, setShowTooltip] = useState(false)
  const [outlineRect, setOutlineRect] = useState<DOMRect | null>(null)
  const mousePositionRef = useRef({ x: 0, y: 0 })
  const dropdownRef = useRef<DropdownState | null>(null)
  const isOptionPressedRef = useRef(false)

  // Keep refs in sync with state
  useEffect(() => {
    dropdownRef.current = dropdown
  }, [dropdown])

  useEffect(() => {
    isOptionPressedRef.current = isOptionPressed
  }, [isOptionPressed])

  useEffect(() => {
    if (typeof window === "undefined") return

    const updateOutlineAtPosition = (x: number, y: number, force: boolean = false) => {
      if (!force && !isOptionPressed) return

      const elementAtPoint = document.elementFromPoint(x, y) as HTMLElement

      if (!elementAtPoint) {
        setHoveredElement(null)
        setOutlineRect(null)
        return
      }

      if (
        elementAtPoint.closest("[data-click-to-component-outline]") ||
        elementAtPoint.closest("[data-click-to-component-dropdown]")
      ) {
        return
      }

      if (hoveredElement !== elementAtPoint) {
        setHoveredElement(elementAtPoint)
      }
      const rect = elementAtPoint.getBoundingClientRect()
      setOutlineRect(rect)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Alt") {
        setIsOptionPressed(true)
        const pos = mousePositionRef.current
        if (pos.x > 0 && pos.y > 0) {
          updateOutlineAtPosition(pos.x, pos.y, true)
        }
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Alt") {
        setIsOptionPressed(false)
        setShowTooltip(false)
        // If dropdown is open, keep the outline visible using the reference element
        if (dropdown?.referenceElement) {
          setHoveredElement(dropdown.referenceElement)
          const rect = dropdown.referenceElement.getBoundingClientRect()
          setOutlineRect(rect)
        } else {
          // Only clear outline if dropdown is not open
          setHoveredElement(null)
          setOutlineRect(null)
        }
      }
    }

    const handleMouseMove = (event: MouseEvent) => {
      const pos = { x: event.clientX, y: event.clientY }
      mousePositionRef.current = pos
      setMousePosition(pos)
      updateOutlineAtPosition(event.clientX, event.clientY)
    }

    const handleScroll = () => {
      // Update outline position on scroll, whether dropdown is open or not
      if (hoveredElement) {
        const rect = hoveredElement.getBoundingClientRect()
        setOutlineRect(rect)
      } else if (dropdown?.referenceElement) {
        // If dropdown is open, update outline based on reference element
        const rect = dropdown.referenceElement.getBoundingClientRect()
        setOutlineRect(rect)
      }
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      if (target?.closest("[data-click-to-component-dropdown]")) {
        return
      }

      if (!event.altKey) {
        if (dropdown) {
          setDropdown(null)
        }
        return
      }

      if (target?.closest("[data-click-to-component-outline]")) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const elementAtPoint = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement

      if (!elementAtPoint) return

      const fiber = findReactFiber(elementAtPoint)
      if (!fiber) {
        return
      }

      const componentInfo = extractComponentInfo(fiber, elementAtPoint, projectRoot)

      setDropdown({
        show: true,
        x: event.clientX,
        y: event.clientY + 10,
        hierarchy: componentInfo.hierarchy,
        editor,
        referenceElement: elementAtPoint,
      })

      // Keep the outline visible when dropdown is open
      setHoveredElement(elementAtPoint)
      const rect = elementAtPoint.getBoundingClientRect()
      setOutlineRect(rect)
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Use refs to get current values
        const currentDropdown = dropdownRef.current
        const currentIsOptionPressed = isOptionPressedRef.current

        if (currentDropdown) {
          event.preventDefault()
          event.stopPropagation()
          setDropdown(null)
          // Clear outline when closing dropdown
          setHoveredElement(null)
          setOutlineRect(null)
        } else if (currentIsOptionPressed) {
          event.preventDefault()
          event.stopPropagation()
          // If inspecting but no dropdown, stop inspecting
          setIsOptionPressed(false)
          setShowTooltip(false)
          setHoveredElement(null)
          setOutlineRect(null)
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    document.addEventListener("mousemove", handleMouseMove, true)
    document.addEventListener("click", handleClick, true)
    window.addEventListener("scroll", handleScroll, true)
    // Use capture phase for escape to ensure it's handled
    window.addEventListener("keydown", handleEscape, true)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
      document.removeEventListener("mousemove", handleMouseMove, true)
      document.removeEventListener("click", handleClick, true)
      window.removeEventListener("scroll", handleScroll, true)
      window.removeEventListener("keydown", handleEscape, true)
    }
  }, [editor, isOptionPressed, hoveredElement, dropdown, projectRoot])

  useEffect(() => {
    if (isOptionPressed && !dropdown) {
      const timer = setTimeout(() => {
        setShowTooltip(true)
      }, 10)
      return () => clearTimeout(timer)
    } else {
      setShowTooltip(false)
    }
  }, [isOptionPressed, dropdown])

  // Update outline when dropdown opens
  useEffect(() => {
    if (dropdown?.referenceElement) {
      const rect = dropdown.referenceElement.getBoundingClientRect()
      setOutlineRect(rect)
      setHoveredElement(dropdown.referenceElement)
    }
  }, [dropdown])

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsOptionPressed(false)
        setShowTooltip(false)
        setHoveredElement(null)
        setOutlineRect(null)
      }
    }

    const handleBlur = () => {
      setIsOptionPressed(false)
      setShowTooltip(false)
      setHoveredElement(null)
      setOutlineRect(null)
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.addEventListener("blur", handleBlur)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("blur", handleBlur)
    }
  }, [])

  const handleItemClick = useCallback(
    (item: ComponentHierarchyItem) => {
      if (!item.fullPath) {
        return
      }

      const openUrl = getEditorUrl(item.fullPath, editor)

      if (openUrl) {
        window.location.href = openUrl
        setDropdown(null)
      }
    },
    [editor],
  )

  const OUTLINE_OFFSET = 4

  return (
    <>
      {((isOptionPressed && !dropdown) || dropdown) &&
        outlineRect &&
        createPortal(
          <div
            data-click-to-component-outline
            className="fixed pointer-events-none border-2 border-solid border-blue-400 rounded-sm shadow-[0_0_8px_rgba(0,122,204,0.3)]"
            style={{
              left: `${outlineRect.left - OUTLINE_OFFSET}px`,
              top: `${outlineRect.top - OUTLINE_OFFSET}px`,
              width: `${outlineRect.width + OUTLINE_OFFSET * 2}px`,
              height: `${outlineRect.height + OUTLINE_OFFSET * 2}px`,
              borderRadius: `${OUTLINE_OFFSET * 2}px`,
              zIndex: 9999999,
            }}
          />,
          document.body,
        )}

      {isOptionPressed && !dropdown && showTooltip && (
        <div
          className="fixed pointer-events-none z-[10000] bg-black/80 text-white dark:bg-white dark:text-black px-1 py-0.5 rounded text-[11px] whitespace-nowrap shadow-md"
          style={{
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y - 30}px`,
            transform: "translateX(-50%)",
            zIndex: 10000000,
          }}
        >
          Click to inspect
        </div>
      )}

      {dropdown && (
        <ComponentDropdown
          x={dropdown.x}
          y={dropdown.y}
          hierarchy={dropdown.hierarchy}
          referenceElement={dropdown.referenceElement}
          onItemClick={handleItemClick}
          onClose={() => setDropdown(null)}
        />
      )}
    </>
  )
}
